import { useEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Seedling, LeafDivider } from '@/components/Botanical.tsx'
import { Quill, Trash } from '@/components/Icons.tsx'
import { api, ApiError } from '@/lib/api.ts'
import { useAuth } from '@/state/AuthContext.tsx'
import { longDate, timeAgo, initials } from '@/lib/format.ts'
import type { Entry, Member, Notebook } from '@/lib/types.ts'

export default function NotebookDetail() {
  const { id = '' } = useParams()
  const { user } = useAuth()
  const [notebook, setNotebook] = useState<Notebook | null>(null)
  const [members, setMembers] = useState<Member[]>([])
  const [entries, setEntries] = useState<Entry[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const textarea = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    let cancelled = false
    Promise.all([api.notebooks.list(), api.notebooks.entries(id)])
      .then(([meta, { entries }]) => {
        if (cancelled) return
        setNotebook(meta.notebooks.find((n) => n.id === id) ?? null)
        setMembers(meta.members)
        setEntries(entries)
      })
      .catch(() => { if (!cancelled) setError('This notebook could not be opened.') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [id])

  const byId = useMemo(() => new Map(members.map((m) => [m.id, m])), [members])

  async function write(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const text = textarea.current?.value.trim() ?? ''
    if (!text) return
    setSaving(true)
    setError(null)
    try {
      const { entry } = await api.notebooks.write(id, text)
      setEntries((prev) => [entry, ...prev])
      if (textarea.current) {
        textarea.current.value = ''
        textarea.current.style.height = 'auto'
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not save.')
    } finally {
      setSaving(false)
    }
  }

  /** Cmd/Ctrl+Enter saves, so you never have to reach for the mouse mid-thought. */
  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.currentTarget.form?.requestSubmit()
    }
  }

  async function erase(entryId: string) {
    const previous = entries
    setEntries((prev) => prev.filter((e) => e.id !== entryId))
    try {
      await api.notebooks.erase(id, entryId)
    } catch {
      setEntries(previous)
    }
  }

  if (loading) return <p className="pt-16 text-whisper">Opening…</p>
  if (!notebook) return <p className="pt-16 text-terracotta">{error ?? 'No such notebook.'}</p>

  return (
    <>
      <div className="max-w-measure animate-unfurl pb-8 pt-6">
        <Link to="/notebooks" className="eyebrow transition-colors hover:text-sage">← All notebooks</Link>
        <h1 className="mt-4 font-display text-[2.5rem] leading-[1.08]">{notebook.title}</h1>
        {notebook.prompt && <p className="prose-soft mt-4">{notebook.prompt}</p>}
      </div>

      <form onSubmit={write} className="sheet max-w-measure p-5">
        <textarea
          ref={textarea}
          name="body"
          rows={3}
          placeholder="Say it here…"
          onKeyDown={onKeyDown}
          onInput={(e) => {
            const el = e.currentTarget
            el.style.height = 'auto'
            el.style.height = `${el.scrollHeight}px`
          }}
          className="w-full resize-none bg-transparent font-body text-[1.02rem] leading-[1.7] text-ink outline-none placeholder:text-whisper"
        />
        <div className="mt-3 flex items-center justify-between gap-4 border-t border-linen pt-3">
          <span className="text-[0.72rem] text-whisper">
            Signed as <span style={{ color: user?.accent }}>{user?.displayName}</span> · ⌘↵ to save
          </span>
          <button type="submit" disabled={saving} className="btn-primary py-2 text-[0.85rem]">
            <Quill className="h-4 w-4" />
            {saving ? 'Saving…' : 'Add it'}
          </button>
        </div>
      </form>

      {error && <p className="mt-4 text-[0.88rem] text-terracotta">{error}</p>}

      {entries.length === 0 ? (
        <div className="mt-16 flex flex-col items-center gap-4 text-center">
          <Seedling tone="var(--c-sage)" className="h-16 w-auto opacity-50" />
          <p className="prose-soft max-w-[22rem]">Nothing here yet. Whatever you put first will look far less daunting once it exists.</p>
        </div>
      ) : (
        <ol className="mt-14 max-w-measure space-y-9">
          {entries.map((entry, i) => {
            const author = byId.get(entry.authorId)
            const mine = entry.authorId === user?.id
            return (
              <li key={entry.id} className="group animate-unfurl" style={{ animationDelay: `${Math.min(i, 8) * 50}ms` }}>
                <div className="flex items-center gap-2.5">
                  <span
                    className="grid h-6 w-6 shrink-0 place-items-center rounded-full text-[0.55rem] tracking-wider text-paper"
                    style={{ background: author?.accent ?? 'var(--c-whisper)' }}
                  >
                    {initials(author?.displayName ?? '?')}
                  </span>
                  <span className="text-[0.82rem] text-quiet">{author?.displayName ?? 'Someone'}</span>
                  <span className="text-[0.72rem] text-whisper" title={longDate(entry.createdAt)}>
                    {timeAgo(entry.createdAt)}
                  </span>
                  {mine && (
                    <button
                      onClick={() => void erase(entry.id)}
                      aria-label="Delete this entry"
                      className="ml-auto rounded-full p-1.5 text-whisper opacity-0 transition-all hover:text-terracotta focus-visible:opacity-100 group-hover:opacity-100"
                    >
                      <Trash className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>

                <p
                  className="mt-2.5 whitespace-pre-wrap border-l-2 pl-4 text-[1.02rem] leading-[1.75] text-ink/90"
                  style={{ borderColor: author?.accent ?? 'var(--c-linen)' }}
                >
                  {entry.body}
                </p>
              </li>
            )
          })}
        </ol>
      )}

      <LeafDivider tone="var(--c-sage)" className="mx-auto mt-20 h-4 w-40 opacity-30" />
    </>
  )
}
